// src/index.js
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, HashRouter, unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import './index.css'; // Import Tailwind CSS
import App from './App';
import { deployment } from './config/deployment';
import createTranslatingHistory from './config/translatingHistory';

// Use HashRouter for static file distributions (file:// compatible)
const Router = process.env.REACT_APP_HASH_ROUTER === 'true' ? HashRouter : BrowserRouter;

// On a public-domain deployment (e.g. www.abcgruppen.se/merch reverse-proxied
// to /clothing/abc-se) the router runs on internal /clothing/[brand] paths via
// a translating history, while the address bar keeps the public sub-path. Off
// those domains `deployment` is null and the classic Router is used unchanged.
const translatingHistory = deployment ? createTranslatingHistory(deployment) : null;

// DS Header/Footer/Hyperlink render plain <a href="/clothing/…"> anchors that
// bypass the router. On a public deployment, intercept unmodified left-clicks
// on those and route them through the translating history (SPA nav on the
// public sub-path instead of a full load of an unproxied /clothing/… URL).
if (translatingHistory) {
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const anchor = e.target.closest && e.target.closest('a[href]');
    if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
    const href = anchor.getAttribute('href');
    if (!href || !href.startsWith('/clothing/')) return;
    e.preventDefault();
    translatingHistory.push(href);
  });
}

// Stable viewport height: set --fixed-svh to the smallest observed viewport height
(function setupFixedSVH() {
  const docEl = document.documentElement;
  const getVH = () => Math.round(window.visualViewport ? window.visualViewport.height : window.innerHeight);

  let baseline = getVH();
  const apply = (h) => docEl.style.setProperty('--fixed-svh', `${h}px`);
  apply(baseline);

  const onResize = () => {
    const h = getVH();
    // Only decrease to avoid growth when URL bar collapses
    if (h < baseline) {
      baseline = h;
      apply(baseline);
    }
  };

  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('scroll', onResize, { passive: true });
  window.addEventListener('orientationchange', () => {
    // Re-evaluate after orientation settles
    setTimeout(() => {
      baseline = getVH();
      apply(baseline);
    }, 200);
  });
})();

const container = document.getElementById('root');
const root = createRoot(container);

const routerFuture = {
  v7_startTransition: true,
  v7_relativeSplatPath: true
};

root.render(
  <React.StrictMode>
    {translatingHistory ? (
      <HistoryRouter history={translatingHistory} future={routerFuture}>
        <App />
      </HistoryRouter>
    ) : (
      <Router future={routerFuture}>
        <App />
      </Router>
    )}
  </React.StrictMode>
);
